import React, {FC, useEffect, useState} from 'react'
import {Button, Modal} from 'react-bootstrap'
import {Platform, RequirementItem} from 'src/interfaces/Platform.interface'
import InputFieldComponent from './InputFieldComponent'
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks'
import {handleOauthForConnectPlatformAsync} from 'src/redux/actions/authActions'
import {shopifyDetailsForOauth} from 'src/utils/constants'
import useAppToast from 'src/hooks/useAppToast'
import ConnectPlatformSelectInput from './ConnectPlatformSelectInput'
import AuthRequirementSelectInput from './AuthRequirementSelectInput'
import {fetchCountriesAsync, fetchTemuCountryAsync} from 'src/redux/actions/accountActions'
import {getLogicBrokerPartnersAsync} from 'src/redux/actions/platformActions'
import { useCustomerAccess } from 'src/hooks/useCustomerAccess'
import {
  rememberImpersonationReturnPath,
  resolveOAuthWindowTarget,
} from 'src/utils/impersonationTokens'
import { shouldShowAction, getActionButtonProps, getFormFieldDisabled } from 'src/utils/customerAccessUtils'
import {
  SHIPPHQ_ADMIN_PORTAL_CONNECT_MESSAGE,
  buildConnectionChannelState,
  deriveOAuthStoreFlags,
  isShippHqAdminPortalOnlyPlatform,
} from 'src/utils/platformConnectionValidation'
import {getPlatformConnectionGuideUrl} from 'src/utils/platformConnectionGuideUrl'

type ConnectPlatformModalPropsType = {
  isVisible: boolean
  nature?: string
  closeModal: () => void
  data: Platform | null
  initialValues?: Record<string, any>
  existingPlatformId?: number | null
  channelNameReadOnly?: boolean
  submitLabel?: string
  connectionNotice?: {
    type: 'warning' | 'danger'
    message: string
  } | null
  // Add a function to handle received data from child component
  onDataReceived: (
    data: any,
    friendlyName: string,
    connection_type?: string,
    extras?: Record<string, any>
  ) => void
  // onUpdateState: (newValue: boolean) => void
  isReauth?: boolean
}
type dataCollectorType = {
  id: number
  name: string
  value: string
  connection_type?: any
}

const getCountryListFromPayload = (payload: any) => {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.results?.data)) return payload.results.data
  if (Array.isArray(payload?.results)) return payload.results
  return []
}

const mapCountryOptions = (payload: any) =>
  getCountryListFromPayload(payload)
    .map((country: any) => {
      const name = country?.name ?? country?.country ?? country?.label
      const code =
        country?.country_iso ?? country?.code ?? country?.country_code ?? country?.value
      return name ? {label: name, value: code ?? name} : null
    })
    .filter((option: {label: string; value: string} | null): option is {label: string; value: string} =>
      Boolean(option)
    )

const ConnectPlatformModal: FC<ConnectPlatformModalPropsType> = ({
  isVisible,
  data,
  nature,
  closeModal,
  initialValues,
  existingPlatformId,
  channelNameReadOnly,
  submitLabel = 'Connect',
  connectionNotice,
  onDataReceived,
  isReauth = false,
}) => {
  const [inputData, setInputData] = useState({
    friendlyName: '',
    intput_type: '',
    connection_type: 'api_key',
    country: '',
    country_code: '',
    country_iso: '',
    country_region: '',
    Region: '',
  })
  const [loading, setLoading] = useState(false)
  const dispatch = useAppDispatch()
  const {temuCountryData} = useAppSelector((state) => state.fetchTemuCountry)
  const {
    mainStoreData,
    accountingStoreData,
    shippingStoreData,
    erpStoreData,
    omsStoreData,
    ticketStoreData,
    fetchedConnectedPlatformData,
  } = useAppSelector((state) => state.fetchConnectedPlatform)
  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const customerAccess = useCustomerAccess()
  const {successToast, warningToast} = useAppToast()
  const {platformSettingsLoading} = useAppSelector((state) => state.createPlatformSettings)
  const {platformData, logicBrokerPartnerLoading, logicBrokerPartnerData} = useAppSelector(
    (state) => state.platform
  )
  const [isConnectingUsingOauth, setIsConnectingUsingOauth] = useState<boolean>(
    data?.isOauth ?? false
  )
  const [domainField, setDomainField] = useState(false)
  const [logicBrokerCompanyName, setLogicBrokerCompanyName] = useState('')
  const [logicBrokerPartnerId, setLogicBrokerPartnerId] = useState('')
  const [lastLogicBrokerLookup, setLastLogicBrokerLookup] = useState({key: '', accountType: ''})
  const [countryNameOptions, setCountryNameOptions] = useState<{label: string; value: string}[]>([])
  const [checkState, setCheckState] = useState({
    oauth: false,
    apiKey: false,
  })
  const shouldPreferApiKeyConnection = ['WOOCOMMERCE', 'WALMART'].includes(data?.name ?? '')
  const is_oauth_recommended =
    data?.name === 'WALMART'
      ? '(Recommended only for US marketplaces)'
      : shouldPreferApiKeyConnection
      ? ''
      : '(Recommended)'
  const is_api_key_recommended = shouldPreferApiKeyConnection ? '(Recommended)' : ''
  const supportsConnectionTypeToggle = Boolean(data?.isAllowMultiAuth || data?.name === 'WALMART')
  const canSubmitViaOauth = Boolean(data?.isOauth || data?.name === 'WALMART')
  const [datacollector, setDataCollector] = useState<dataCollectorType[] | []>([]) // Array to store collected data
  const currentPlatformName = data?.name ?? ''
  const normalizedPlatformName = currentPlatformName.toUpperCase()
  const currencyPlatforms = ['FARFETCH']
  const accountModePlatforms = ['ALLEGRO', 'LOGICBROKER', 'XERO']
  const authRequirements: RequirementItem[] = data?.available_platform_auth_requirement ?? []

  const matchesPlatform = (...platforms: string[]) =>
    platforms.some((platform) => normalizedPlatformName === platform.toUpperCase())
  const isShippHqAdminPortalOnly = isShippHqAdminPortalOnlyPlatform(data?.name)

  const shouldHideAuthRequirement = (requirement: RequirementItem) => {
    const normalizedName = (requirement.name ?? '').toLowerCase()
    const normalizedDisplay = (requirement.display_name ?? '').toLowerCase()
    const hasAccount = normalizedName.includes('account') || normalizedDisplay.includes('account')
    const hasMode = normalizedName.includes('mode') || normalizedDisplay.includes('mode')
    if (matchesPlatform('LOGICBROKER')) {
      return normalizedName.includes('currency') || normalizedDisplay.includes('currency')
    }
    if (matchesPlatform('EBAY')) {
      const hasCurrency =
        normalizedName.includes('currency') || normalizedDisplay.includes('currency')
      if (hasCurrency) return true
      return hasAccount && hasMode
    }
    if (!matchesPlatform('SHOPWARE')) return false
    return hasAccount && hasMode
  }

  const visibleAuthRequirements = authRequirements.filter(
    (requirement) => !shouldHideAuthRequirement(requirement)
  )
  const shouldCollectAuthRequirementsBeforeOauth =
    visibleAuthRequirements.length > 0 &&
    Boolean(data?.isOauth) &&
    !supportsConnectionTypeToggle

  const hasCurrencyRequirement =
    matchesPlatform(...currencyPlatforms) &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return normalizedName.includes('currency') || normalizedDisplay.includes('currency')
    })

  const hasAccountModeRequirement =
    matchesPlatform(...accountModePlatforms) &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      const hasAccount = normalizedName.includes('account') || normalizedDisplay.includes('account')
      const hasMode = normalizedName.includes('mode') || normalizedDisplay.includes('mode')
      return matchesPlatform('XERO') ? hasMode : hasAccount && hasMode
    })
  const modeFieldLabel = matchesPlatform('XERO') ? 'Mode' : 'Account Mode'
  const modeFieldName = matchesPlatform('XERO') ? 'mode' : 'account_mode'

  const shouldRenderCurrencyFallback =
    matchesPlatform(...currencyPlatforms) && !hasCurrencyRequirement
  const shouldRenderAccountModeFallback =
    matchesPlatform(...accountModePlatforms) && !hasAccountModeRequirement
  const hasCloverRegionRequirement =
    matchesPlatform('CLOVER') &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return normalizedName.includes('region') || normalizedDisplay.includes('region')
    })
  const shouldRenderCloverRegion = matchesPlatform('CLOVER') && !hasCloverRegionRequirement
  const countryFallbackPlatforms = ['NOONV2', 'FAIRE', 'FRUUGOV2']
  const hasCountryRequirement =
    matchesPlatform(...countryFallbackPlatforms) &&
    !!data?.available_platform_auth_requirement?.some(({name, display_name}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return normalizedName.includes('country') || normalizedDisplay.includes('country')
    })
  const cloverRegionOptions = [
    {label: 'North America: US and Canada (api.clover.com)', value: 'US'},
    {label: 'Europe (api.eu.clover.com)', value: 'EU'},
    {label: 'Latin America (api.la.clover.com)', value: 'LA'},
  ]
  const noonV2CountryOptions = [
    {label: 'Saudi Arabia (KSA)', value: 'SA'},
    {label: 'United Arab Emirates (UAE)', value: 'AE'},
  ]
  const amazonSellerCountryRequirement = platformData
    ?.find((platform) => platform.name === 'AMAZONSELLER')
    ?.available_platform_auth_requirement?.find(({name, display_name, input_type}) => {
      const normalizedName = (name ?? '').toLowerCase()
      const normalizedDisplay = (display_name ?? '').toLowerCase()
      return (
        input_type === 'select' &&
        (normalizedName.includes('country') || normalizedDisplay.includes('country'))
      )
    }) ?? {id: 190, input_type: 'select', name: 'country', display_name: 'Country Name'}
  const shouldRenderCountryFallback =
    !hasCountryRequirement &&
    matchesPlatform(...countryFallbackPlatforms)

  const shouldRenderEbayCountrySelector = matchesPlatform('EBAY')

  // Set default checked values
  useEffect(() => {
    setCheckState({
      oauth: is_oauth_recommended === '(Recommended)',
      apiKey: is_api_key_recommended === '(Recommended)',
    })
    if (['WOOCOMMERCE', 'WALMART'].includes(data?.name ?? '')) setIsConnectingUsingOauth(false)
  }, [])

  useEffect(() => {
    if (!isVisible) return
    if (!initialValues) return
    setInputData((prev) => ({...prev, ...initialValues}))
  }, [isVisible, initialValues])

  // const handleClick = () => {
  //   onUpdateState(true) // Call callback function to update parent state
  //   console.log('true', onUpdateState)
  // }

  const handleRadioChange =
    (type: 'oauth' | 'apiKey') => (ev: React.ChangeEvent<HTMLInputElement>) => {
      setCheckState({
        oauth: type === 'oauth' ? ev.target.checked : !ev.target.checked,
        apiKey: type === 'apiKey' ? ev.target.checked : !ev.target.checked,
      })

      if (type === 'oauth' && !isConnectingUsingOauth) {
        setIsConnectingUsingOauth(ev.target.checked)
      } else if (type === 'apiKey' && isConnectingUsingOauth) {
        setIsConnectingUsingOauth(!ev.target.checked)
      }
    }

  const handleInputChange = (id: any, name: string, value: string) => {
    setInputData({...inputData, [id]: value, [name]: value})
    if (name === 'proxies') {
      value === 'SANDBOX' ? setDomainField(true) : setDomainField(false)
    }
  }

  const handleFruugoV2CountryChange = (countryCode: string) => {
    const selectedCountry = countryNameOptions.find((country) => country.value === countryCode)
    setInputData({
      ...inputData,
      country: selectedCountry?.label ?? '',
      country_code: countryCode,
    })
  }

  const isValueEmpty = (value: any) =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '')

  const getRequirementValue = (...keys: string[]) => {
    for (const key of keys) {
      const directValue = (inputData as Record<string, any>)[key]
      if (!isValueEmpty(directValue)) {
        return directValue
      }
    }

    const normalizedKeys = keys.map((key) => key.toLowerCase())
    const matchedRequirement = data?.available_platform_auth_requirement?.find(
      ({name, display_name}) => {
        const normalizedName = name ? name.toLowerCase() : ''
        const normalizedDisplay = display_name ? display_name.toLowerCase() : ''
        return normalizedKeys.some((key) => {
          return (
            normalizedName.includes(key) ||
            key.includes(normalizedName) ||
            normalizedDisplay.includes(key) ||
            key.includes(normalizedDisplay)
          )
        })
      }
    )

    if (matchedRequirement) {
      const candidates = [
        matchedRequirement.name,
        matchedRequirement.display_name,
        String(matchedRequirement.id),
      ].filter(Boolean) as string[]

      for (const candidate of candidates) {
        const value = (inputData as Record<string, any>)[candidate]
        if (!isValueEmpty(value)) {
          return value
        }
      }
    }

    return undefined
  }

  const getAuthRequirementNamesByKeywords = (keywords: string[]) => {
    return authRequirements
      .filter(({name, display_name}) => {
        const normalizedName = (name ?? '').toLowerCase()
        const normalizedDisplay = (display_name ?? '').toLowerCase()
        return keywords.every(
          (keyword) =>
            normalizedName.includes(keyword) || normalizedDisplay.includes(keyword)
        )
      })
      .map(({name}) => name)
      .filter(Boolean)
  }

  const getWalmartOauthDefaults = () => {
    const defaults: Record<string, any> = {
      country_domain: '.Com',
      marketplace: 'United States',
    }

    const countryDomainRequirementNames = getAuthRequirementNamesByKeywords([
      'country',
      'domain',
    ])
    const marketplaceRequirementNames = getAuthRequirementNamesByKeywords(['marketplace'])

    countryDomainRequirementNames.forEach((name) => {
      defaults[name] = '.Com'
    })

    marketplaceRequirementNames.forEach((name) => {
      defaults[name] = 'United States'
    })

    return defaults
  }

  const getLogicBrokerLookupInputs = () => {
    const subscriptionKey = String(
      getRequirementValue('subscription_key', 'subscription key', 'key', 'client_secret') ?? ''
    ).trim()
    const accountMode = String(getRequirementValue('account_mode', 'account mode', 'mode') ?? '')
      .trim()
      .toLowerCase()
    return {subscriptionKey, accountMode}
  }

  const fetchLogicBrokerPartner = async (force = false) => {
    if (!matchesPlatform('LOGICBROKER')) {
      return null
    }

    const {subscriptionKey, accountMode} = getLogicBrokerLookupInputs()
    if (!subscriptionKey || !accountMode) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      return null
    }

    if (
      !force &&
      lastLogicBrokerLookup.key === subscriptionKey &&
      lastLogicBrokerLookup.accountType === accountMode &&
      logicBrokerPartnerId
    ) {
      return {partnerId: logicBrokerPartnerId, companyName: logicBrokerCompanyName}
    }

    setLastLogicBrokerLookup({key: subscriptionKey, accountType: accountMode})
    await dispatch(getLogicBrokerPartnersAsync({account_type: accountMode, key: subscriptionKey}))
    if (force && !logicBrokerPartnerData?.partnerId) {
      warningToast('Unable to resolve LogicBroker company for provided values')
      return null
    }
    return {
      partnerId: logicBrokerPartnerData?.partnerId || '',
      companyName: logicBrokerPartnerData?.companyName || '',
    }
  }

  const shouldUseOauthForReauth = isReauth && matchesPlatform('EBAY')

  const handleSave = async () => {
    if (matchesPlatform('LOGICBROKER')) {
      if (logicBrokerPartnerLoading) {
        warningToast('Please wait while LogicBroker company is being fetched')
        return
      }
      if (!logicBrokerPartnerId) {
        warningToast('Please select Account Mode and wait for company details')
        return
      }
      if (isValueEmpty(getRequirementValue('country_code'))) {
        warningToast('Country is required')
        return
      }
    }

    const ebayCountryIsoValue = matchesPlatform('EBAY')
      ? getRequirementValue('country_iso')
      : undefined
    const accountModeValue = matchesPlatform(...accountModePlatforms)
      ? getRequirementValue('mode', 'account_mode', 'account mode')
      : undefined
    const requireEbayValidation = matchesPlatform('EBAY') && !isReauth
    const requireAccountModeValidation =
      matchesPlatform(...accountModePlatforms) && !shouldUseOauthForReauth

    if (requireEbayValidation) {
      if (isValueEmpty(ebayCountryIsoValue)) {
        warningToast('Country is required')
        return
      }
    }

    if (requireAccountModeValidation && isValueEmpty(accountModeValue)) {
      warningToast(`${modeFieldLabel} is required`)
      return
    }

    const cloverRegionValue = getRequirementValue('region', 'Region', 'country_region')
    if (matchesPlatform('CLOVER')) {
      if (isValueEmpty(cloverRegionValue)) {
        warningToast('Region is required')
        return
      }
    }
    if (matchesPlatform(...countryFallbackPlatforms)) {
      const countryValue = getRequirementValue('country')
      if (isValueEmpty(countryValue)) {
        warningToast('Country is required')
        return
      }
    }

    // Collect data from inputData and update dataCollector state
    const collectedData = visibleAuthRequirements.length
      ? visibleAuthRequirements.map(({id, name}) => ({
          id,
          value: inputData[id],
          name: name,
        }))
      : []
    if (matchesPlatform('CLOVER') && !isValueEmpty(cloverRegionValue)) {
      const regionIndex = collectedData.findIndex(({name}) =>
        ['region', 'country_region'].includes((name ?? '').toLowerCase())
      )

      if (regionIndex >= 0) {
        collectedData[regionIndex] = {
          ...collectedData[regionIndex],
          name: 'country_region',
          value: cloverRegionValue,
        }
      } else {
        collectedData.push({
          id: -102,
          value: cloverRegionValue,
          name: 'country_region',
        })
      }
    }
    if (matchesPlatform(...countryFallbackPlatforms)) {
      const countryValue = getRequirementValue('country')
      if (!isValueEmpty(countryValue)) {
        const countryCodeValue = getRequirementValue('country_code')
        const countryIndex = collectedData.findIndex(
          ({name}) => (name ?? '').toLowerCase() === 'country'
        )
        if (countryIndex >= 0) {
          collectedData[countryIndex] = {
            ...collectedData[countryIndex],
            name: 'country',
            value: countryValue,
          }
        } else {
          collectedData.push({
            id: -103,
            value: countryValue,
            name: 'country',
          })
        }
        if (matchesPlatform('FRUUGOV2') && !isValueEmpty(countryCodeValue)) {
          const countryCodeIndex = collectedData.findIndex(
            ({name}) => (name ?? '').toLowerCase() === 'country_code'
          )
          if (countryCodeIndex >= 0) {
            collectedData[countryCodeIndex] = {
              ...collectedData[countryCodeIndex],
              name: 'country_code',
              value: countryCodeValue,
            }
          } else {
            collectedData.push({
              id: -104,
              value: countryCodeValue,
              name: 'country_code',
            })
          }
        }
      }
    }
    if (matchesPlatform('EBAY') && !isValueEmpty(ebayCountryIsoValue)) {
      const countryIsoIndex = collectedData.findIndex(
        ({name}) => (name ?? '').toLowerCase() === 'country_iso'
      )
      if (countryIsoIndex >= 0) {
        collectedData[countryIsoIndex] = {
          ...collectedData[countryIsoIndex],
          name: 'country_iso',
          value: ebayCountryIsoValue,
        }
      } else {
        collectedData.push({
          id: -106,
          value: ebayCountryIsoValue,
          name: 'country_iso',
        })
      }
    }
    if (matchesPlatform(...accountModePlatforms) && !isValueEmpty(accountModeValue)) {
      const hasAccountModeEntry = collectedData.some(({name}) => {
        const normalizedName = (name ?? '').toLowerCase()
        const normalizedDisplay = normalizedName
        const hasAccount = normalizedName.includes('account') || normalizedDisplay.includes('account')
        const hasMode = normalizedName.includes('mode') || normalizedDisplay.includes('mode')
        return matchesPlatform('XERO') ? hasMode : hasAccount && hasMode
      })

      if (!hasAccountModeEntry) {
        collectedData.push({
          id: 'account_mode' as any,
          value: accountModeValue,
          name: matchesPlatform('XERO') ? 'mode' : 'account_mode',
        })
      }
    }
    if (matchesPlatform('LOGICBROKER')) {
      const countryCodeValue = getRequirementValue('country_code')
      if (!isValueEmpty(countryCodeValue)) {
        const countryCodeIndex = collectedData.findIndex(
          ({name}) => (name ?? '').toLowerCase() === 'country_code'
        )
        if (countryCodeIndex >= 0) {
          collectedData[countryCodeIndex] = {
            ...collectedData[countryCodeIndex],
            name: 'country_code',
            value: countryCodeValue,
          }
        } else {
          collectedData.push({
            id: -105 as any,
            value: countryCodeValue,
            name: 'country_code',
          })
        }
      }
      if (logicBrokerPartnerId) {
        let hasClientIdEntry = false
        collectedData.forEach((item) => {
          if ((item.name ?? '').toLowerCase() === 'client_id') {
            item.value = logicBrokerPartnerId
            hasClientIdEntry = true
          }
        })
        if (!hasClientIdEntry) {
          collectedData.push({
            id: 'client_id' as any,
            value: logicBrokerPartnerId,
            name: 'client_id',
          })
        }
      }
    }
    setDataCollector(collectedData)

    //Create default channel name, if user dont add any name
    if (inputData['friendlyName'] == '') {
      const count = fetchedConnectedPlatformData?.filter((item) => item.name === data?.name).length
      const value = count > 0 ? `${data?.name}_${count}` : data?.name
      if (value) inputData['friendlyName'] = value
    }
    onDataReceived(collectedData, inputData.friendlyName, inputData.connection_type, {
      country_iso: ebayCountryIsoValue,
      account_mode: accountModeValue,
      mode: accountModeValue,
    })
    // closeModal() // Close modal after saving
  }

  const handleOauth = () => {
    const payload = {available_platform_name: data?.name, user_id: fetchedUserAccountData?.id}
    if (existingPlatformId !== undefined && existingPlatformId !== null) {
      payload['platform_id'] = existingPlatformId
    }
    //  let url = 'https://disconnect-v2.myshopify.com/admin/oauth/authorize?client_id=0be8fcf4e4cdc1ba5bf5087cc62287fb&scope=read_orders,write_orders,read_customers,write_customers,read_products,write_products&redirect_uri=https://react2.constacloud.com/handle-oauth-callback-restricted&state=0WZ48kIyGl9AOMDNuHq2zGxG75ErTdkLvBtFtUsEKC4'
    //  let redirectedUrl = 'http://localhost:3011/handle-oauth-callback-restricted?code=3832868a3ca99b7fb2cd329c03ae9528&hmac=cc93be7994208243552577cf9d9f07994c0458b214e4c0969c5a3dbbbb52fa4d&host=YWRtaW4uc2hvcGlmeS5jb20vc3RvcmUvZGlzY29ubmVjdC12Mg&shop=disconnect-v2.myshopify.com&state=0WZ48kIyGl9AOMDNuHq2zGxG75ErTdkLvBtFtUsEKC4&timestamp=1715929169'
    //  const handle =  window.open(url,'_blank','popup=yes,top=100,left=300, status=yes, toolbar=yes, menubar=yes, location=yes,addressbar=yes' )
    //  console.log('handle', handle?.window.window.opener )
    //  return
    if (inputData['friendlyName'] == '') {
      //Create default channel name, if user dont add any name
      const count = fetchedConnectedPlatformData?.filter((item) => item.name === data?.name).length
      const value = count > 0 ? `${data?.name}_${count}` : data?.name
      if (value) inputData['friendlyName'] = value
    }
    if (matchesPlatform('EBAY')) {
      const countryIsoValue = getRequirementValue('country_iso')
      if (isValueEmpty(countryIsoValue)) {
        warningToast('Country is required')
        return
      }
      payload['country_iso'] = countryIsoValue
    }
    if (matchesPlatform('XERO')) {
      const modeValue = getRequirementValue('mode', 'account_mode', 'account mode')
      if (isValueEmpty(modeValue)) {
        warningToast('Mode is required')
        return
      }
      payload['mode'] = modeValue
    }
    if (matchesPlatform('CLOVER')) {
      const regionValue = getRequirementValue('region', 'Region', 'country_region')
      if (isValueEmpty(regionValue)) {
        warningToast('Region is required')
        return
      }
      payload['country_region'] = regionValue
    }
    if (matchesPlatform(...countryFallbackPlatforms)) {
      const countryValue = getRequirementValue('country')
      if (isValueEmpty(countryValue)) {
        warningToast('Country is required')
        return
      }
      payload['country'] = countryValue
      if (matchesPlatform('FRUUGOV2')) {
        payload['country_code'] = getRequirementValue('country_code') ?? ''
      }
    }
    if (data?.name === 'SHOPIFY') {
      if (inputData['identifier'] == '' || inputData['identifier'] === undefined) {
        warningToast('Domain is required')
        return
      } else {
        payload['shop'] = inputData['identifier']
      }
    }

    if (data?.name === 'ZohoInventory') {
      if (inputData['country'] == '' || inputData['country'] === undefined) {
        warningToast('Country is required')
        return
      } else {
        payload['country'] = inputData['country']
      }
    }

    if (data?.name === 'TIKTOK') {
      if (inputData['country']) {
        payload['country'] = inputData['country']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'TEMU') {
      if (inputData['country']) {
        payload['country'] = inputData['country']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'AIMS360') {
      if (inputData['proxies']) {
        payload['proxies'] = inputData['proxies']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'WOOCOMMERCE') {
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Domain name is required')
        return
      }
    }

    if (
      data?.name === 'AMAZONSELLER' ||
      data?.name === 'AMAZONVENDOR'
    ) {
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'WALMART') {
      Object.assign(payload, getWalmartOauthDefaults())
    }

    if (data?.name === 'SQUARE' || data?.name === 'LIGHTSPEED' || data?.name === 'CIN7OMNI') {
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Currency is required')
        return
      }
    }

    if (data?.name === 'ZALORA') {
      if (inputData['proxies']) {
        payload['proxies'] = inputData['proxies']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'NOON') {
      if (inputData['token_secret']) {
        payload['token_secret'] = inputData['token_secret']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'CDON' || data?.name === 'BOL') {
      if (inputData['country_code']) {
        payload['country_code'] = inputData['country_code']
      } else {
        warningToast('Country is required')
        return
      }
    }

    if (data?.name === 'BOL') {
      if (inputData['country_code']) {
        payload['country_code'] = inputData['country_code']
      } else {
        warningToast('Country is required')
        return
      }
      if (inputData['currency']) {
        payload['currency'] = inputData['currency']
      } else {
        warningToast('Currency is required')
        return
      }
    }

    if (data?.name === 'SHOPEE') {
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Country is required')
        return
      }
      if (inputData['proxies']) {
        payload['proxies'] = inputData['proxies']
      } else {
        warningToast('Seller Type is required')
        return
      }
      if (inputData['proxies']) {
        payload['proxies'] = inputData['proxies']
      } else {
        warningToast('Seller Type is required')
        return
      }
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
    }

    if (data?.name === 'AMAZONMCF') {
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Country is required')
        return
      }
    }

    // if(data?.name==='AMAZONVENDOR' ){
    //   if(inputData['proxies']){
    //     payload['proxies'] = inputData['proxies']
    //   }else{
    //     warningToast("Selling Party Id is required")
    //     return
    //   }
    // }

    if (data?.name === 'ACUMATICA') {
      if (!inputData['identifier']) {
        warningToast('Domain is required')
        return
      }
      if (!inputData['proxies']) {
        warningToast('Proxies is required')
        return
      }
      if (!inputData['client_id']) {
        warningToast('Client ID is required')
        return
      }
      if (!inputData['client_secret']) {
        warningToast('Client Secret is required')
        return
      }

      payload['identifier'] = inputData['identifier']
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
      payload['proxies'] = inputData['proxies']
      payload['version'] = inputData['version']
    }

    if (data?.name === 'LAZADA') {
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Country is required')
        return
      }
    }
    if (data?.name === 'TRADEME') {
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
      if (inputData['identifier']) {
        payload['identifier'] = inputData['identifier']
      } else {
        warningToast('Currency is required')
        return
      }
    }

    if (data?.name === 'ALLEGRO' || data?.name === 'JUMIAVENDOR') {
      payload['client_id'] = inputData['client_id']
      payload['client_secret'] = inputData['client_secret']
    }

    if (shouldCollectAuthRequirementsBeforeOauth) {
      for (const requirement of visibleAuthRequirements) {
        const fieldName = requirement.name ?? ''
        const value =
          getRequirementValue(fieldName, requirement.display_name ?? '') ??
          (fieldName ? inputData[fieldName] : undefined)
        if (isValueEmpty(value)) {
          warningToast(`${requirement.display_name || fieldName || 'Required field'} is required`)
          return
        }
        if (fieldName) {
          payload[fieldName] = value
        }
      }
    }

    setLoading(true)
    dispatch(handleOauthForConnectPlatformAsync({...payload})).then((response) => {
      if (response.payload?.data && response.payload?.status_code === 200) {
        const channelState = buildConnectionChannelState({
          mainStoreData,
          accountingStoreData,
          shippingStoreData,
          erpStoreData,
          omsStoreData,
          ticketStoreData,
          fetchedConnectedPlatformData,
        })
        const connectNature = nature ?? 'SECONDARY'
        const storeFlags = deriveOAuthStoreFlags(data, connectNature, undefined, channelState)

        localStorage.setItem(
          shopifyDetailsForOauth,
          JSON.stringify({
            ...data,
            platform_id: existingPlatformId ?? null,
            friendlyName: inputData.friendlyName,
            isMainStore: storeFlags.isMainStore,
            isAccountingStore: storeFlags.isAccountingStore,
            isShippingStore: storeFlags.isShippingStore,
            isErpStore: storeFlags.isErpStore,
            isOmsStore: storeFlags.isOmsStore,
            isTicketStore: storeFlags.isTicketStore,
            userId: fetchedUserAccountData?.id,
            zohoAuthDomain: inputData['identifier'] ? inputData['identifier'] : '',
            client_id: inputData['client_id'],
            client_secret: inputData['client_secret'],
            proxies: inputData['proxies'],
            version: inputData['version'],
            ...(matchesPlatform('EBAY')
              ? {country_iso: inputData['country_iso']}
              : {
                  country: matchesPlatform('FAIRE', 'FRUUGOV2')
                    ? inputData['country']
                    : Number(inputData['country']),
                }),
            ...(data?.name === 'WALMART' ? getWalmartOauthDefaults() : {}),
          })
        )
        setLoading(false)
        rememberImpersonationReturnPath()
        const oauthTarget = resolveOAuthWindowTarget(data?.name)
        window.open(response.payload?.data?.url, oauthTarget)
        // window.open(response.payload?.data?.url,"_blank",'height=400,width=400,top=100,left=300')
        // window.location.href = response.payload?.data?.url
      }
      if (response.payload?.data?.status_code == 400) {
        warningToast(response.payload?.data?.data?.message)
      }
      setLoading(false)
    })
  }

  useEffect(() => {
    if (data?.name === 'TEMU') dispatch(fetchTemuCountryAsync())
  }, [data?.name])

  useEffect(() => {
    if (!matchesPlatform('FRUUGOV2', 'LOGICBROKER', 'EBAY')) return
    dispatch(fetchCountriesAsync() as any)
      .then((response: any) => {
        setCountryNameOptions(mapCountryOptions(response?.payload))
      })
      .catch(() => setCountryNameOptions([]))
  }, [dispatch, normalizedPlatformName])

  useEffect(() => {
    if (!matchesPlatform('LOGICBROKER')) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      setLastLogicBrokerLookup({key: '', accountType: ''})
      return
    }

    const {subscriptionKey, accountMode} = getLogicBrokerLookupInputs()
    if (!subscriptionKey || !accountMode) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      return
    }

    setLogicBrokerCompanyName('')
    setLogicBrokerPartnerId('')
    const timer = setTimeout(() => {
      fetchLogicBrokerPartner(false)
    }, 500)

    return () => clearTimeout(timer)
  }, [
    data?.name,
    inputData['subscription_key'],
    inputData['key'],
    inputData['client_secret'],
    inputData['account_mode'],
    inputData['mode'],
  ])

  useEffect(() => {
    if (!matchesPlatform('LOGICBROKER')) {
      return
    }
    if (!logicBrokerPartnerData) {
      return
    }
    if (
      logicBrokerPartnerData.key !== lastLogicBrokerLookup.key ||
      logicBrokerPartnerData.accountType !== lastLogicBrokerLookup.accountType
    ) {
      return
    }
    if (!logicBrokerPartnerData.partnerId) {
      setLogicBrokerCompanyName('')
      setLogicBrokerPartnerId('')
      return
    }
    setLogicBrokerCompanyName(logicBrokerPartnerData.companyName)
    setLogicBrokerPartnerId(logicBrokerPartnerData.partnerId)
    setInputData((prev) => ({...prev, client_id: logicBrokerPartnerData.partnerId}))
  }, [logicBrokerPartnerData, lastLogicBrokerLookup.key, lastLogicBrokerLookup.accountType, data?.name])

  return (
    <>
      <Modal
        size='lg'
        backdrop='static'
        centered
        show={isVisible}
        onHide={() => {
          closeModal()
          setInputData({
            friendlyName: '',
            intput_type: '',
            connection_type: '',
            country: '',
            country_code: '',
            country_iso: '',
            country_region: '',
            Region: '',
          })
          setLogicBrokerCompanyName('')
          setLogicBrokerPartnerId('')
          setLastLogicBrokerLookup({key: '', accountType: ''})
        }}
      >
        <form
          onSubmit={(ev) => {
            ev.preventDefault()
            if (isShippHqAdminPortalOnly) {
              return
            }
            if (shouldUseOauthForReauth) {
              handleOauth()
              return
            }
            if (!canSubmitViaOauth || isConnectingUsingOauth === false) {
              // alert('without oauth')
              handleSave()
            } else {
              // alert('with oauth')
              handleOauth()
            }
          }}
        >
          <Modal.Header>
            <Modal.Title>Connect {data?.display_name}</Modal.Title>
            <div>
              <a
                target='_blank'
                href={getPlatformConnectionGuideUrl(data?.name, data?.help_href)}
                className='btn btn-link text-primary fs-6 fw-bold ' rel="noreferrer"
              >
                Click Here to See {data?.display_name} Connection Guide
              </a>
            </div>
          </Modal.Header>

          {/* only for presta shop connection type  */}

          {data?.name == 'PRESTASHOP' ? (
            <>
              <div className='row px-10 pe-0 py-2'>
                <div className='col-md-3 col-4'>
                  <label className='form-label mb-0'>Connection Type</label>
                </div>
                <div className='col-sm-9 col-12 ps-0' style={{alignContent: 'space-evenly'}}>
                  <div className='d-flex align-items-center'>
                    <label htmlFor='default' className=' ms-2'>
                      <input
                        id='default'
                        checked={inputData?.connection_type == 'api_key'}
                        className='form-check-input mx-1'
                        name='prestaconnectionType'
                        type='radio'
                        onChange={() => {
                          handleInputChange('default', 'connection_type', 'api_key')
                        }}
                      />
                      Default (Recommended)
                    </label>
                    <label htmlFor='custom' className=' me-2'>
                      <input
                        id='custom'
                        checked={inputData?.connection_type == 'ws_key'}
                        className='form-check-input form-check-input-lg mx-1'
                        name='prestaconnectionType'
                        type='radio'
                        onChange={() => {
                          handleInputChange('custom', 'connection_type', 'ws_key')
                        }}
                      />
                      Custom (for Self-hosted & older version setup){' '}
                    </label>
                  </div>
                </div>
              </div>
              <div className='separator my-1'></div>
            </>
          ) : null}

          {supportsConnectionTypeToggle ? (
            <>
              <div className='row px-10 pe-0 py-2'>
                <div className='col-md-3 col-4'>
                  <label className='form-label mb-0'>Connection Type</label>
                </div>
                <div className='col-sm-9 col-12 ps-0' style={{alignContent: 'space-evenly'}}>
                  <div className='d-flex align-items-center'>
                    <label htmlFor='withoutOauth' className=' ms-2'>
                      <input
                        id='withoutOauth'
                        checked={checkState.apiKey}
                        className='form-check-input mx-1'
                        name='checkOauthType'
                        type='radio'
                        onChange={handleRadioChange('apiKey')}
                      />
                      Via API Keys {is_api_key_recommended}
                    </label>
                    <label htmlFor='withOauth' className=' me-2'>
                      <input
                        id='withOauth'
                        checked={checkState.oauth}
                        className='form-check-input form-check-input-lg mx-1'
                        name='checkOauthType'
                        type='radio'
                        onChange={handleRadioChange('oauth')}
                      />
                      Secure Connection via OAuth {is_oauth_recommended}
                    </label>
                  </div>
                </div>
              </div>
              <div className='separator my-1'></div>
            </>
          ) : null}
          {data?.auth_route_name === 'chrome-extension' ? (
            <>
              <div
                className='d-flex justify-content-center align-items-center text-secondary'
                style={{height: '10vh'}}
              >
                <h5>Go to Commercium Extension to connect {data?.display_name}</h5>
              </div>
              <div className='text-end mx-2 my-2'>
                <Button
                  disabled={loading}
                  type='button'
                  className='btn-sm'
                  variant='secondary'
                  onClick={() => {
                    closeModal()
                    setInputData({
                      friendlyName: '',
                      intput_type: '',
                      connection_type: '',
                      country: '',
                      country_code: '',
                      country_iso: '',
                      country_region: '',
                      Region: '',
                    })
                  }}
                >
                  Close
                </Button>
              </div>
            </>
          ) : (
            <>
              <Modal.Body>
                <div className=''>
                  {connectionNotice?.message ? (
                    <div className={`alert alert-${connectionNotice.type} py-3 mb-5`}>
                      {connectionNotice.message}
                    </div>
                  ) : null}
                  {isShippHqAdminPortalOnly ? (
                    <div className='alert alert-info py-3 mb-0'>{SHIPPHQ_ADMIN_PORTAL_CONNECT_MESSAGE}</div>
                  ) : (
                    <>
                  {(visibleAuthRequirements.length > 0 ||
                    data?.isOauth ||
                    shouldRenderCloverRegion ||
                    shouldRenderCountryFallback ||
                    shouldRenderEbayCountrySelector) ? (
                    <div className='row px-4 mb-3'>
                      {' '}
                      {/* Use flexbox for side-by-side */}
                      <div className='col-md-3 col-6 '>
                        {' '}
                        {/* Apply col-md-6 for half column width */}
                        <label
                          className='form-label'
                          // style={{
                          //   marginRight: '10px', // Adjust as needed
                          //   fontWeight: 'bold',
                          //   fontSize: '1.25rem', // Adjust as needed
                          // }}
                        >
                          Channel Name
                        </label>
                      </div>
                      <div className='col-sm-6 col-12 '>
                        {' '}
                        {/* Apply col-md-6 for half column width */}
                        <input
                          type='text'
                          // required
                          className='form-control form-control-solid'
                          placeholder='Just enter any name for your reference to this channel'
                          value={inputData.friendlyName}
                          readOnly={!!channelNameReadOnly}
                          onChange={(e) => {
                            if (!channelNameReadOnly) {
                              handleInputChange('friendlyName', 'friendlyName', e.target.value)
                            }
                          }}
                        />
                      </div>
                    </div>
                  ) : null}
                  {(visibleAuthRequirements.length > 0 ||
                    shouldRenderCurrencyFallback ||
                    shouldRenderAccountModeFallback ||
                    shouldRenderCloverRegion ||
                    shouldRenderCountryFallback ||
                    shouldRenderEbayCountrySelector) && (
                      <>
                        {/* <hr /> */}
                        {/* <br /> */}
                        <div className='row '>
                          {visibleAuthRequirements.length > 0 &&
                            (!canSubmitViaOauth ||
                            isConnectingUsingOauth === false ||
                            shouldCollectAuthRequirementsBeforeOauth
                              ? visibleAuthRequirements.map(
                                  (item: RequirementItem, index) => {
                                  if (data?.name === 'TEMU') {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <ConnectPlatformSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                            options={
                                              item.display_name == 'Select Region'
                                                ? [
                                                    {label: 'US', value: 'US'},
                                                    {label: 'EU', value: 'EU'},
                                                    {label: 'GLOBAL', value: 'GLOBAL'},
                                                  ]
                                                : temuCountryData.data?.map((data: any) => ({
                                                    value: data.region_id,
                                                    label: data.country_name as any,
                                                  })) || []
                                            }
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (data?.name === 'AIMS360') {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <ConnectPlatformSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                            options={[
                                              {label: 'West US', value: 'apiwest'},
                                              {label: 'East US', value: 'apieast'},
                                            ]}
                                          />
                                        </div>
                                      )
                                    }
                                   }

                                  if (data?.name === 'SHIPBOB') {
                                    if (item.input_type === 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <AuthRequirementSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            data={data}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''}
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (matchesPlatform(...currencyPlatforms, ...accountModePlatforms)) {
                                    if (item.input_type == 'select') {
                                      const normalizedName = (item.name || '').toLowerCase()
                                      const normalizedDisplay = (
                                        item.display_name || ''
                                      ).toLowerCase()

                                      if (
                                        normalizedName.includes('currency') ||
                                        normalizedDisplay.includes('currency')
                                      ) {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <AuthRequirementSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              data={data}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                            />
                                          </div>
                                        )
                                      }

                                      const hasAccount =
                                        normalizedName.includes('account') ||
                                        normalizedDisplay.includes('account')
                                      const hasMode =
                                        normalizedName.includes('mode') ||
                                        normalizedDisplay.includes('mode')

                                      if (matchesPlatform('XERO') ? hasMode : hasAccount && hasMode) {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'Sandbox', value: 'SANDBOX'},
                                                {label: 'Live', value: 'LIVE'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }
                                  }

                                  if (data?.name === 'VIRTUAL_STOCK') {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <AuthRequirementSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            data={data}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (['GUNBROKER'].includes(data?.name ?? '')) {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <ConnectPlatformSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                            options={[
                                              {label: 'Sandbox', value: 'SANDBOX'},
                                              {label: 'Live', value: 'LIVE'},
                                            ]}
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (['ZALORA'].includes(data?.name ?? '')) {
                                    if (item.input_type == 'select') {
                                      if (item.name === 'proxies') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <AuthRequirementSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              data={data}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                            />
                                          </div>
                                        )
                                      } else {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'Sandbox', value: 'SANDBOX'},
                                                {label: 'Live', value: 'LIVE'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }
                                  }

                                  if (data?.name === 'FRUUGO') {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <ConnectPlatformSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                            options={[
                                              {label: 'GBP', value: 'GBP'},
                                              {label: 'INR', value: 'INR'},
                                              {label: 'USD', value: 'USD'},
                                            ]}
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (data?.name === 'SHOPIFY') {
                                    if (item.name == 'identifier') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <InputFieldComponent
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            placeholder='eg. storename.myshopify.com'
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(inputValue) =>
                                              handleInputChange(
                                                inputValue.id,
                                                inputValue.name,
                                                inputValue.value
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (data?.name === 'WOOCOMMERCE') {
                                    if (item.name == 'identifier') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <InputFieldComponent
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            placeholder='Enter only domain eg. storename.mystore.com'
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(inputValue) =>
                                              handleInputChange(
                                                inputValue.id,
                                                inputValue.name,
                                                inputValue.value
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (data?.name === 'PRESTASHOP') {
                                    if (item.name == 'identifier') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <InputFieldComponent
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            placeholder='Enter domain without https:// eg. mystore-prestashop.com'
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(inputValue) =>
                                              handleInputChange(
                                                inputValue.id,
                                                inputValue.name,
                                                inputValue.value
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (
                                    [
                                      'AMAZONSELLER',
                                      'FAIRE',
                                      'VIVINO',
                                      'SHOPEE',
                                      'EMAG',
                                      'BACKMARKET',
                                      'WALMART',
                                      'ACUMATICA',
                                      'SQUARE',
                                      'CDON',
                                      'AMAZONMCF',
                                      'AMAZONVENDOR',
                                      'TRADEME',
                                      'NOON',
                                      'NOONV2',
                                      'CIN7OMNI',
                                      'BOL',
                                      'KAUFLAND',
                                      'UNLEASHED',
                                      'DSCO',
                                      'TRENDYOL',
                                      'NEWEGG',
                                    ].includes(data?.name ?? '')
                                  ) {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <AuthRequirementSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            data={data}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (['WAYFAIR', 'REFURBED', 'ICONIC'].includes(data?.name ?? '')) {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <ConnectPlatformSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                            options={[
                                              {label: 'Sandbox', value: 'SANDBOX'},
                                              {label: 'Live', value: 'LIVE'},
                                            ]}
                                          />
                                        </div>
                                      )
                                    }
                                  }

                                  if (data?.name === 'REFURBED') {
                                    if (!domainField) {
                                      if (item.name !== 'identifier') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <InputFieldComponent
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(inputValue) =>
                                                handleInputChange(
                                                  inputValue.id,
                                                  inputValue.name,
                                                  inputValue.value
                                                )
                                              }
                                            />
                                          </div>
                                        )
                                      }
                                    } else {
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <InputFieldComponent
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(inputValue) =>
                                              handleInputChange(
                                                inputValue.id,
                                                inputValue.name,
                                                inputValue.value
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    }
                                  } else {
                                    if (item.input_type == 'select') {
                                      return (
                                        <div key={item.id} className={`mb-3 row justify-content-end`}>
                                          <AuthRequirementSelectInput
                                            display_name={item.display_name}
                                            id={item.id}
                                            data={data}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''}
                                            onDataChange={(ev) => {
                                              handleInputChange(item.id, item.name, ev.target.value)
                                            }}
                                          />
                                        </div>
                                      )
                                    }

                                    return (
                                      <div key={item.id} className={`mb-3 row justify-content-end`}>
                                        <InputFieldComponent
                                          display_name={item.display_name}
                                          id={item.id}
                                          input_type={item.input_type}
                                          name={item.name}
                                          inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                          onDataChange={(inputValue) =>
                                            handleInputChange(
                                              inputValue.id,
                                              inputValue.name,
                                              inputValue.value
                                            )
                                          }
                                        />
                                      </div>
                                    )
                                  }
                                }
                              )
                            : [
                                'SHOPIFY',
                                'EBAY',
                                'ZohoInventory',
                                'TIKTOK',
                                'WOOCOMMERCE',
                                'TEMU',
                                'AMAZONSELLER',
                                'FAIRE',
                                'AMAZONVENDOR',
                                'SHOPEE',
                                'GUNBROKER',
                                'LAZADA',
                                'ALLEGRO',
                                'WALMART',
                                'ACUMATICA',
                                'SQUARE',
                                'CDON',
                                'AMAZONMCF',
                                'TRADEME',
                                'JUMIAVENDOR',
                                'LIGHTSPEED',
                                'VIRTUAL_STOCK',
                                'TRENDYOL',
                              ].includes(data?.name ?? '')
                            ? visibleAuthRequirements.map(
                                (item: RequirementItem, index) => {
                                  if (item.display_name === 'Shopify Domain') {
                                    return (
                                      <div key={item.id} className={`mb-3 row justify-content-end`}>
                                        <InputFieldComponent
                                          display_name={item.display_name}
                                          id={item.id}
                                          input_type={item.input_type}
                                          placeholder={'eg. storename.myshopify.com'}
                                          name={item.name}
                                          inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                          onDataChange={(inputValue) =>
                                            handleInputChange(
                                              inputValue.id,
                                              inputValue.name,
                                              inputValue.value
                                            )
                                          }
                                        />
                                      </div>
                                    )
                                  } else {
                                    if (
                                      ['LAZADA', 'ALLEGRO', 'JUMIAVENDOR', 'AMAZONVENDOR'].includes(
                                        data?.name ?? ''
                                      )
                                    ) {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <AuthRequirementSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              data={data}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                            />
                                          </div>
                                        )
                                      }
                                      return (
                                        <div
                                          key={item.id}
                                          className={`mb-3 row justify-content-end`}
                                        >
                                          <InputFieldComponent
                                            display_name={item.display_name}
                                            id={item.id}
                                            input_type={item.input_type}
                                            name={item.name}
                                            inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                            onDataChange={(inputValue) =>
                                              handleInputChange(
                                                inputValue.id,
                                                inputValue.name,
                                                inputValue.value
                                              )
                                            }
                                          />
                                        </div>
                                      )
                                    }

                                    if (data?.name === 'TIKTOK') {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.name,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'UK', value: 'UK'},
                                                {label: 'US', value: 'US'},
                                                {label: 'OTHERS', value: 'OTHERS'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (data?.name === 'AIMS360') {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'West US', value: 'apiwest'},
                                                {label: 'East US', value: 'apieast'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (matchesPlatform(...currencyPlatforms, ...accountModePlatforms)) {
                                      if (item.input_type == 'select') {
                                        const normalizedName = (item.name || '').toLowerCase()
                                        const normalizedDisplay = (
                                          item.display_name || ''
                                        ).toLowerCase()

                                        if (
                                          normalizedName.includes('currency') ||
                                          normalizedDisplay.includes('currency')
                                        ) {
                                          return (
                                            <div
                                              key={item.id}
                                              className={`mb-3 row justify-content-end`}
                                            >
                                              <AuthRequirementSelectInput
                                                display_name={item.display_name}
                                                id={item.id}
                                                data={data}
                                                input_type={item.input_type}
                                                name={item.name}
                                                inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                                onDataChange={(ev) => {
                                                  handleInputChange(
                                                    item.id,
                                                    item.name,
                                                    ev.target.value
                                                  )
                                                }}
                                              />
                                            </div>
                                          )
                                        }

                                        const hasAccount =
                                          normalizedName.includes('account') ||
                                          normalizedDisplay.includes('account')
                                        const hasMode =
                                          normalizedName.includes('mode') ||
                                          normalizedDisplay.includes('mode')

                                        if (matchesPlatform('XERO') ? hasMode : hasAccount && hasMode) {
                                          return (
                                            <div
                                              key={item.id}
                                              className={`mb-3 row justify-content-end`}
                                            >
                                              <ConnectPlatformSelectInput
                                                display_name={item.display_name}
                                                id={item.id}
                                                input_type={item.input_type}
                                                name={item.name}
                                                inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                                onDataChange={(ev) => {
                                                  handleInputChange(
                                                    item.id,
                                                    item.name,
                                                    ev.target.value
                                                  )
                                                }}
                                                options={[
                                                  {label: 'Sandbox', value: 'SANDBOX'},
                                                  {label: 'Live', value: 'LIVE'},
                                                ]}
                                              />
                                            </div>
                                          )
                                        }
                                      }
                                    }

                                    if (data?.name === 'VIRTUAL_STOCK') {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <AuthRequirementSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              data={data}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (data?.name === 'GUNBROKER') {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'Sandbox', value: 'sandbox'},
                                                {label: 'Live', value: 'live'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (data?.name === 'FRUUGO') {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'GBP', value: 'GBP'},
                                                {label: 'INR', value: 'INR'},
                                                {label: 'USD', value: 'USD'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (data?.name === 'WOOCOMMERCE') {
                                      if (item.name == 'identifier') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <InputFieldComponent
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              placeholder='Enter only domain eg. storename.mystore.com'
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(inputValue) =>
                                                handleInputChange(
                                                  inputValue.id,
                                                  inputValue.name,
                                                  inputValue.value
                                                )
                                              }
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (data?.name === 'TEMU') {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <ConnectPlatformSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.name,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                              options={[
                                                {label: 'US', value: 'US'},
                                                {label: 'EU', value: 'EU'},
                                                {label: 'GLOBAL', value: 'GLOBAL'},
                                              ]}
                                            />
                                          </div>
                                        )
                                      }
                                    }

                                    if (
                                      [
                                        'AMAZONSELLER',
                                        'FAIRE',
                                        'VIVINO',
                                        'ZohoInventory',
                                        'SQUARE',
                                        'CDON',
                                        'AMAZONMCF',
                                        'TRADEME',
                                        'LIGHTSPEED',
                                        'SHOPEE',
                                        'ACUMATICA',
                                        'TRENDYOL',
                                      ].includes(data?.name ?? '')
                                    ) {
                                      if (item.input_type == 'select') {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <AuthRequirementSelectInput
                                              display_name={item.display_name}
                                              id={item.id}
                                              data={data}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(ev) => {
                                                handleInputChange(
                                                  item.id,
                                                  item.name,
                                                  ev.target.value
                                                )
                                              }}
                                            />
                                          </div>
                                        )
                                      } else {
                                        return (
                                          <div
                                            key={item.id}
                                            className={`mb-3 row justify-content-end`}
                                          >
                                            <InputFieldComponent
                                              display_name={item.display_name}
                                              id={item.id}
                                              input_type={item.input_type}
                                              name={item.name}
                                              inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                              onDataChange={(inputValue) =>
                                                handleInputChange(
                                                  inputValue.id,
                                                  inputValue.name,
                                                  inputValue.value
                                                )
                                              }
                                            />
                                          </div>
                                        )
                                      }
                                    }
                                  }
                                  // if(process.env.REACT_APP_ENV === 'staging'){
                                  // }else{
                                  //   return(
                                  //     <div key={item.id} className={`mb-3 row justify-content-end`}>
                                  //       <InputFieldComponent
                                  //         display_name={item.display_name}
                                  //         id={item.id}
                                  //         input_type={item.input_type}
                                  //         name={item.name}
                                  //         inputValue={inputData[item.name] || ''} // Pass the value from inputData
                                  //         onDataChange={(inputValue) =>
                                  //           handleInputChange(inputValue.id, inputValue.name, inputValue.value)
                                  //         }
                                  //       />
                                  //     </div>
                                  //   )
                                  // }
                                  }
                                )
                              : null)}
                          {shouldRenderCurrencyFallback && (
                            <div className={`mb-3 row justify-content-end`}>
                              <AuthRequirementSelectInput
                                display_name='Currency'
                                id={-100}
                                data={data as Platform}
                                input_type='select'
                                name='currency'
                                inputValue={inputData['currency'] || ''}
                                onDataChange={(ev) => {
                                  handleInputChange(-100, 'currency', ev.target.value)
                                }}
                              />
                            </div>
                          )}
                          {shouldRenderEbayCountrySelector && (
                            <div className={`mb-3 row justify-content-end`}>
                              <ConnectPlatformSelectInput
                                display_name='Select Country of your eBay Store'
                                id={-106}
                                input_type='select'
                                name='country_iso'
                                inputValue={inputData['country_iso'] || ''}
                                onDataChange={(ev) => {
                                  handleInputChange('country_iso', 'country_iso', ev.target.value)
                                }}
                                options={countryNameOptions}
                              />
                            </div>
                          )}
                          {shouldRenderCloverRegion && (
                            <div className={`mb-3 row justify-content-end`}>
                              <ConnectPlatformSelectInput
                                display_name='Region'
                                id={-102}
                                input_type='select'
                                name='country_region'
                                inputValue={inputData['country_region'] || inputData['Region'] || ''}
                                onDataChange={(ev) => {
                                  handleInputChange('country_region', 'country_region', ev.target.value)
                                }}
                                options={cloverRegionOptions}
                              />
                            </div>
                          )}
                          {shouldRenderCountryFallback && (
                            <div className={`mb-3 row justify-content-end`}>
                              {matchesPlatform('FAIRE') && amazonSellerCountryRequirement ? (
                                <AuthRequirementSelectInput
                                  display_name={
                                    amazonSellerCountryRequirement.display_name ?? 'Country Name'
                                  }
                                  id={amazonSellerCountryRequirement.id}
                                  input_type='select'
                                  data={{...data, name: 'AMAZONSELLER'}}
                                  name='country'
                                  inputValue={inputData['country'] || ''}
                                  onDataChange={(ev) => {
                                    handleInputChange('country', 'country', ev.target.value)
                                  }}
                                />
                              ) : matchesPlatform('FRUUGOV2') ? (
                                <ConnectPlatformSelectInput
                                  display_name='Country Name'
                                  id={-103}
                                  input_type='select'
                                  name='country'
                                  inputValue={inputData['country_code'] || ''}
                                  onDataChange={(ev) => {
                                    handleFruugoV2CountryChange(ev.target.value)
                                  }}
                                  options={countryNameOptions}
                                />
                              ) : (
                                <ConnectPlatformSelectInput
                                  display_name='Country'
                                  id={-103}
                                  input_type='select'
                                  name='country'
                                  inputValue={inputData['country'] || ''}
                                  onDataChange={(ev) => {
                                    handleInputChange('country', 'country', ev.target.value)
                                  }}
                                  options={noonV2CountryOptions}
                                />
                              )}
                            </div>
                          )}
                          {shouldRenderAccountModeFallback && (
                            <div className={`mb-3 row justify-content-end`}>
                              <ConnectPlatformSelectInput
                                display_name={modeFieldLabel}
                                id={-101}
                                input_type='select'
                                name={modeFieldName}
                                inputValue={inputData[modeFieldName] || ''}
                                onDataChange={(ev) => {
                                  handleInputChange(modeFieldName, modeFieldName, ev.target.value)
                                }}
                                options={[
                                  {label: 'Sandbox', value: 'SANDBOX'},
                                  {label: 'Live', value: 'LIVE'},
                                ]}
                              />
                            </div>
                          )}
                          {matchesPlatform('LOGICBROKER') && (
                            <div className={`mb-3 row justify-content-end`}>
                              <ConnectPlatformSelectInput
                                display_name='Country'
                                id={-105}
                                input_type='select'
                                name='country_code'
                                inputValue={inputData['country_code'] || ''}
                                onDataChange={(ev) => {
                                  handleFruugoV2CountryChange(ev.target.value)
                                }}
                                options={countryNameOptions}
                              />
                            </div>
                          )}
                          {matchesPlatform('LOGICBROKER') && (
                            <div className={`mb-3 row justify-content-end`}>
                              <div className='row'>
                                <div className='col-md-3 col-6'>
                                  <label className='form-label'>Company</label>
                                </div>
                                <div className='col-sm-6 col-12'>
                                  <input
                                    type='text'
                                    className='form-control form-control-solid'
                                    readOnly
                                    value={
                                      logicBrokerPartnerLoading
                                        ? 'Fetching company...'
                                        : logicBrokerCompanyName || ''
                                    }
                                    placeholder='Company'
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  {data && ['VEEQO'].includes(data?.name) && (
                    <small className='text-secondary fs-6 ms-4'>
                      Log in to Veeqo in another tab before connecting it here.
                    </small>
                  )}
                  {data && ['QUICKBOOKSDESKTOP'].includes(data?.name) && (
                    <div className='row ps-4 pe-0 mb-3'>
                      <div className='col-12 px-0'>
                        <small className='text-secondary fs-6 d-block'>
                          <strong>Note:</strong> This is not your QuickBooks login password. Create any
                          password here — you'll enter this same password later inside QuickBooks Web
                          Connector when you install the connection file we provide.
                        </small>
                      </div>
                    </div>
                  )}
                    </>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  disabled={loading}
                  type='button'
                  className='btn-sm'
                  variant='secondary'
                  onClick={() => {
                    closeModal()
                    setInputData({
                      friendlyName: '',
                      intput_type: '',
                      connection_type: '',
                      country: '',
                      country_code: '',
                      country_iso: '',
                      country_region: '',
                      Region: '',
                    })
                    setLogicBrokerCompanyName('')
                    setLogicBrokerPartnerId('')
                    setLastLogicBrokerLookup({key: '', accountType: ''})
                  }}
                >
                  Close
                </Button>
                {/* { (data?.isOauth === false || isConnectingUsingOauth ===false) ?
              (
                <Button disabled={platformSettingsLoading} type='submit' className='btn btn-success btn-sm' variant='primary' onClick={handleSave}>
                  { platformSettingsLoading ? 'Connecting...' : 'Connect'}
                </Button>
              ) : (
                <button disabled={loading} className='btn btn-success btn-sm' type='submit'  onClick={()=>handleOauth()} >{ loading ? 'Connecting...' : 'Connect'}</button>
              )
            } */}

                {['QUICKBOOKS'].includes(data?.name!) ? (
                  shouldShowAction(customerAccess, 'create') && (() => {
                    const quickbooksButtonProps = getActionButtonProps(customerAccess, 'create')
                    const isDisabled = loading || platformSettingsLoading || quickbooksButtonProps.disabled || getFormFieldDisabled(customerAccess)
                    return (
                      <img
                        src='/media/logos/quickbooks_button.svg'
                        alt='QUICKBOOKS'
                        style={{
                          height: '32px',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.5 : 1,
                          ...quickbooksButtonProps.style
                        }}
                        title={quickbooksButtonProps.title}
                        onClick={() => {
                          if (!isDisabled) {
                            if (shouldUseOauthForReauth) {
                              handleOauth()
                              return
                            }
                            if (!canSubmitViaOauth || isConnectingUsingOauth === false) {
                              handleSave()
                            } else {
                              handleOauth()
                            }
                          }
                        }}
                      />
                    )
                  })()
                ) : !isShippHqAdminPortalOnly && shouldShowAction(customerAccess, 'create') && (() => {
                  const connectButtonProps = getActionButtonProps(customerAccess, 'create')
                  const buttonLabel = submitLabel || 'Connect'
                  return (
                    <button
                      {...connectButtonProps}
                      disabled={
                        loading ||
                        platformSettingsLoading ||
                        connectButtonProps.disabled ||
                        getFormFieldDisabled(customerAccess)
                      }
                      className='btn btn-success btn-sm'
                      type='submit'
                      style={connectButtonProps.style}
                      title={connectButtonProps.title}
                    >
                      {loading || platformSettingsLoading ? 'Connecting...' : buttonLabel}
                    </button>
                  )
                })()}
              </Modal.Footer>
            </>
          )}
        </form>
      </Modal>
    </>
  )
}

export default ConnectPlatformModal

